import React, { useState } from "react";
import { Mail, Trash2, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PasswordInput } from "./PasswordInput";

const AccountSettingsPanel = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const isLocal = user?.provider === "local";

    // Email change
    const [newEmail, setNewEmail] = useState("");
    const [currentPw, setCurrentPw] = useState("");
    const [emailLoading, setEmailLoading] = useState(false);

    // Account delete
    const [confirmDelete, setConfirmDelete] = useState("");
    const [deletePw, setDeletePw] = useState("");
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const submitEmailChange = async (e) => {
        e.preventDefault();
        if (!newEmail) return;
        setEmailLoading(true);
        try {
            const { data } = await api.post("/my/email-change", { new_email: newEmail, password: currentPw || "noop" });
            toast.success(data.message || "Confirmation email sent.");
            setNewEmail(""); setCurrentPw("");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not start email change");
        } finally {
            setEmailLoading(false);
        }
    };

    const submitDelete = async () => {
        if (confirmDelete !== "DELETE") {
            toast.error('Please type "DELETE" exactly to confirm.');
            return;
        }
        setDeleteLoading(true);
        try {
            await api.delete("/my/account", { data: { confirm: confirmDelete, password: deletePw || null } });
            toast.success("Account closed. Goodbye.");
            await logout();
            navigate("/", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not close account");
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className="bg-white border border-black/5 rounded-2xl p-6 mt-10" data-testid="account-settings-panel">
            <h2 className="font-heading text-2xl text-[#1C252E] mb-1 tracking-tight">Account settings</h2>
            <p className="text-sm text-[#4A5568] mb-6">Change the email you sign in with, or close your account.</p>

            {/* Email change */}
            <form onSubmit={submitEmailChange} className="border-t border-black/5 pt-6 space-y-4" data-testid="email-change-form">
                <div>
                    <h3 className="font-medium text-[#1C252E] flex items-center gap-2"><Mail className="w-4 h-4 text-[#E53935]" strokeWidth={1.5} /> Change your sign-in email</h3>
                    <p className="text-xs text-[#4A5568] mt-1">Current: <span className="text-[#1C252E] font-medium">{user?.email}</span>. We'll email a confirmation link to your new address.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label htmlFor="new-email" className="sr-only">New email</label>
                        <input
                            id="new-email"
                            type="email"
                            placeholder="New email address"
                            autoComplete="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            required
                            data-testid="account-new-email-input"
                            className="w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                        />
                    </div>
                    {isLocal && (
                        <div>
                            <label htmlFor="email-change-pw" className="sr-only">Current password</label>
                            <PasswordInput
                                id="email-change-pw"
                                placeholder="Current password"
                                autoComplete="current-password"
                                value={currentPw}
                                onChange={(e) => setCurrentPw(e.target.value)}
                                required
                                testId="account-current-pw-input"
                                showLockIcon={false}
                            />
                        </div>
                    )}
                </div>
                <button
                    type="submit"
                    disabled={emailLoading}
                    data-testid="account-email-change-btn"
                    className="bg-[#1C252E] hover:bg-black text-white rounded-full px-5 py-2.5 text-sm font-medium transition-all disabled:opacity-60"
                >
                    {emailLoading ? "Sending…" : "Send confirmation link"}
                </button>
            </form>

            {/* Delete account */}
            <div className="border-t border-red-200 pt-6 mt-8" data-testid="delete-account-section">
                <h3 className="font-medium text-[#E53935] flex items-center gap-2"><Trash2 className="w-4 h-4" strokeWidth={1.5} /> Close your account</h3>
                <p className="text-xs text-[#4A5568] mt-1">
                    This deletes your sign-in. Your donations stay on the public ledger as financial records. Active recurring subscriptions are cancelled.
                </p>

                {!deleteOpen ? (
                    <button
                        type="button"
                        onClick={() => setDeleteOpen(true)}
                        data-testid="account-delete-open-btn"
                        className="mt-3 border border-red-200 text-[#E53935] hover:bg-red-50 rounded-full px-5 py-2.5 text-sm font-medium transition-all"
                    >
                        I want to close my account
                    </button>
                ) : (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 space-y-3" data-testid="account-delete-confirm-box">
                        <div className="flex items-start gap-2 text-sm text-[#1C252E]">
                            <AlertTriangle className="w-4 h-4 text-[#E53935] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                            <span>This cannot be undone. Type <span className="font-mono font-medium">DELETE</span> below and (for password accounts) enter your current password.</span>
                        </div>
                        <input
                            type="text"
                            placeholder='Type "DELETE" to confirm'
                            value={confirmDelete}
                            onChange={(e) => setConfirmDelete(e.target.value)}
                            data-testid="account-delete-confirm-input"
                            className="w-full rounded-lg border border-red-200 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 transition-all"
                        />
                        {isLocal && (
                            <PasswordInput
                                placeholder="Current password"
                                autoComplete="current-password"
                                value={deletePw}
                                onChange={(e) => setDeletePw(e.target.value)}
                                testId="account-delete-pw-input"
                                showLockIcon={false}
                            />
                        )}
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={submitDelete}
                                disabled={deleteLoading || confirmDelete !== "DELETE"}
                                data-testid="account-delete-submit-btn"
                                className="bg-[#E53935] hover:bg-red-700 text-white rounded-full px-5 py-2.5 text-sm font-medium transition-all disabled:opacity-60"
                            >
                                {deleteLoading ? "Closing…" : "Permanently close my account"}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setDeleteOpen(false); setConfirmDelete(""); setDeletePw(""); }}
                                data-testid="account-delete-cancel-btn"
                                className="border border-black/10 text-[#1C252E] hover:bg-[#F4F1EA] rounded-full px-5 py-2.5 text-sm font-medium transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AccountSettingsPanel;
